/**
 * M4 — Cross-tab session ownership via BroadcastChannel.
 *
 * Prevents two tabs on the same sessionUid from firing conflicting
 * stop / start / finalize / payment actions. First tab to announce
 * for a sessionUid becomes the OWNER; subsequent tabs enter
 * READONLY mode and hide the charging control affordances behind a
 * "This session is open in another tab" banner with a takeover
 * button.
 *
 * Contract:
 *   - `createOwnership(sessionUid, { tabId?, onChange })` returns a
 *     small controller `{ role, requestOwnership, releaseOwnership,
 *     close }`.
 *   - `role` starts as `'unknown'` while the announcement handshake
 *     runs, then settles to `'owner'` or `'readonly'`.
 *   - Owner tab heartbeats every 3 s. Readonly tabs mark the owner
 *     stale after 10 s without a heartbeat and re-negotiate.
 *   - `requestOwnership()` broadcasts a takeover — the current owner
 *     downgrades to `readonly` on receipt.
 *   - On tab close (`beforeunload` / `visibilitychange hidden`) the
 *     controller broadcasts a `release` so a peer tab can promote
 *     immediately.
 *   - Falls back to owner-forever when BroadcastChannel is missing
 *     (private mode / older browsers) so single-tab UX is unaffected.
 *
 * The backend idempotency layer is authoritative — this is a
 * DEFENCE-IN-DEPTH UX guard, not a security boundary.
 */

const CHANNEL_NAME_PREFIX = 'gc-session-';
const HEARTBEAT_MS = 3000;
const OWNER_STALE_MS = 10_000;

/**
 * Cryptographically unique tab id. crypto.randomUUID exists in every
 * evergreen browser + jsdom; we fall back to a random string if not.
 */
function makeTabId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * @param {string} sessionUid
 * @param {object} [options]
 * @param {string} [options.tabId]  Override the auto-generated tab id (tests).
 * @param {(role: 'unknown'|'owner'|'readonly') => void} [options.onChange]
 * @returns {{
 *   role: 'unknown' | 'owner' | 'readonly',
 *   sessionUid: string,
 *   tabId: string,
 *   requestOwnership: () => void,
 *   releaseOwnership: () => void,
 *   close: () => void,
 * }}
 */
// Tab ids of the ownership controllers living in THIS document. A
// BroadcastChannel delivers to every other channel object, including ones in
// the same document, so a probe must not mistake this tab for "another tab".
const localTabIds = new Set();

export function createOwnership(sessionUid, options = {}) {
  const tabId = options.tabId || makeTabId();
  localTabIds.add(tabId);
  const onChange = typeof options.onChange === 'function' ? options.onChange : () => {};

  // BroadcastChannel absent → single-tab environment. Skip the whole
  // handshake and settle to owner immediately.
  if (typeof BroadcastChannel === 'undefined' || !sessionUid) {
    const controller = {
      role: sessionUid ? 'owner' : 'unknown',
      sessionUid,
      tabId,
      requestOwnership: () => {},
      releaseOwnership: () => {},
      close: () => {},
    };
    if (sessionUid) setTimeout(() => onChange('owner'), 0);
    return controller;
  }

  const channel = new BroadcastChannel(`${CHANNEL_NAME_PREFIX}${sessionUid}`);
  const state = {
    role: 'unknown',
    lastOwnerHeartbeatAt: 0,
    ownerTabId: null,
  };
  let heartbeatTimer = null;
  let staleCheckTimer = null;
  let announceTimer = null;
  let closed = false;

  function setRole(nextRole) {
    if (state.role === nextRole) return;
    state.role = nextRole;
    controller.role = nextRole;
    try { onChange(nextRole); } catch { /* ignore */ }
  }

  function post(type, extra = {}) {
    if (closed) return;
    try { channel.postMessage({ type, tabId, at: Date.now(), ...extra }); } catch { /* ignore */ }
  }

  function startHeartbeat() {
    stopHeartbeat();
    // Immediately announce so listeners don't wait a full interval
    // to see us.
    post('heartbeat');
    heartbeatTimer = setInterval(() => post('heartbeat'), HEARTBEAT_MS);
  }
  function stopHeartbeat() {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }

  function claimOwnership() {
    state.ownerTabId = tabId;
    setRole('owner');
    startHeartbeat();
  }

  function yieldOwnership(toTabId) {
    state.ownerTabId = toTabId || null;
    setRole('readonly');
    stopHeartbeat();
  }

  function handleMessage(evt) {
    const msg = evt?.data;
    if (!msg || typeof msg !== 'object') return;
    if (msg.tabId === tabId) return;
    switch (msg.type) {
      case 'hello':
        // A new tab is announcing. If we're the owner, respond with
        // our claim so the new tab knows to go readonly.
        if (state.role === 'owner') post('claim');
        break;
      case 'claim':
      case 'heartbeat':
        state.lastOwnerHeartbeatAt = Date.now();
        state.ownerTabId = msg.tabId;
        // If we thought we were the owner but someone else is
        // claiming, yield — takeover semantics.
        if (state.role === 'owner') {
          yieldOwnership(msg.tabId);
        } else {
          setRole('readonly');
        }
        break;
      case 'takeover':
        // A peer explicitly wants ownership. If we're currently
        // owner, downgrade and let them claim.
        if (state.role === 'owner') yieldOwnership(msg.tabId);
        break;
      case 'release':
        // The current owner is leaving. Anyone in readonly can
        // race to claim; we do so after a tiny random delay to
        // reduce collisions.
        if (state.role !== 'owner') {
          const jitter = Math.random() * 200;
          setTimeout(() => {
            if (Date.now() - state.lastOwnerHeartbeatAt > 500) {
              claimOwnership();
            }
          }, jitter);
        }
        break;
      default:
        break;
    }
  }
  channel.addEventListener('message', handleMessage);

  // Announce arrival. If no one responds within a short window,
  // assume no owner exists and claim.
  post('hello');
  announceTimer = setTimeout(() => {
    if (closed) return;
    if (state.role === 'unknown') claimOwnership();
  }, 250);

  // Detect a stale owner (owner tab silently died — e.g. crashed).
  staleCheckTimer = setInterval(() => {
    if (closed) return;
    if (state.role !== 'readonly') return;
    if (state.lastOwnerHeartbeatAt === 0) return;
    if (Date.now() - state.lastOwnerHeartbeatAt > OWNER_STALE_MS) {
      // Owner died. Reset and re-announce; whoever wins the next
      // handshake becomes the new owner.
      state.lastOwnerHeartbeatAt = 0;
      state.ownerTabId = null;
      post('hello');
      // Give the announcement a beat, then claim if still unknown.
      setTimeout(() => {
        if (!closed && state.role !== 'owner' && state.lastOwnerHeartbeatAt === 0) {
          claimOwnership();
        }
      }, 250);
    }
  }, HEARTBEAT_MS);

  // On tab close / hide, release so a peer can promote.
  const releaseOnUnload = () => {
    if (state.role === 'owner') post('release');
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', releaseOnUnload);
    window.addEventListener('pagehide', releaseOnUnload);
  }

  const controller = {
    role: state.role,
    sessionUid,
    tabId,
    requestOwnership() {
      if (state.role === 'owner') return;
      // Broadcast takeover, then claim after a tiny delay so the
      // current owner has a moment to downgrade.
      post('takeover');
      setTimeout(() => { if (!closed && state.role !== 'owner') claimOwnership(); }, 200);
    },
    releaseOwnership() {
      if (state.role === 'owner') {
        post('release');
        yieldOwnership(null);
      }
    },
    close() {
      if (closed) return;
      closed = true;
      releaseOnUnload();
      stopHeartbeat();
      if (announceTimer) clearTimeout(announceTimer);
      if (staleCheckTimer) clearInterval(staleCheckTimer);
      channel.removeEventListener('message', handleMessage);
      try { channel.close(); } catch { /* ignore */ }
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', releaseOnUnload);
        window.removeEventListener('pagehide', releaseOnUnload);
      }
    },
  };
  return controller;
}

/**
 * Resolve true when ANOTHER open tab currently owns `sessionUid`.
 *
 * Announces with a one-off `hello`; a live owner answers with `claim`, and an
 * owner's regular `heartbeat` counts too. Answers from this document's own
 * controllers are ignored. Resolves false when nobody answers within
 * `timeoutMs`, or when BroadcastChannel is unavailable (single-tab
 * environment). Never throws. Used before an automatic action that would
 * affect a session another tab may still be driving.
 */
export function isOwnedByAnotherTab(sessionUid, { timeoutMs = 300 } = {}) {
  if (!sessionUid || typeof BroadcastChannel === 'undefined') return Promise.resolve(false);
  return new Promise((resolve) => {
    let channel;
    let done = false;
    const probeId = makeTabId();
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try { channel?.close(); } catch { /* ignore */ }
      resolve(value);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    try {
      channel = new BroadcastChannel(`${CHANNEL_NAME_PREFIX}${sessionUid}`);
      channel.addEventListener('message', (evt) => {
        const msg = evt?.data;
        if (!msg || typeof msg !== 'object') return;
        if (msg.tabId === probeId || localTabIds.has(msg.tabId)) return;
        if (msg.type === 'claim' || msg.type === 'heartbeat') finish(true);
      });
      channel.postMessage({ type: 'hello', tabId: probeId, at: Date.now() });
    } catch {
      finish(false);
    }
  });
}
